'use client'

import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  CheckSquare,
  Square,
  AlertTriangle,
  Loader2,
  Undo2,
  Check
} from 'lucide-react'
import { cn } from '../../utils/cn'
import { BirhausButton } from '../BirhausButton'
import type { 
  BirhausBulkActionBarProps,
  BulkActionState,
  ProgressInfo,
  UndoAction
} from './types'
import type {
  BirhausBulkAction
} from '../../types/bulk-actions'
import {
  isSimpleAction,
  isUndoableAction
} from '../../types/bulk-actions'

/**
 * BirhausBulkActionBar - Advanced bulk action component with BIRHAUS principles
 * 
 * Features:
 * - Spanish-first labels with English fallbacks
 * - Miller's Law compliance (max 4 bulk actions per bar)
 * - 4-3-1 cognitive rule (4 nav items max, 3 contextual, 1 primary)
 * - Undo-over-confirm patterns (no confirmation dialogs)
 * - Progress tracking for batch operations
 * - Accessibility-first design (WCAG AA+)
 */
export function BirhausBulkActionBar<T = any>({
  // Selection state
  selectedCount = 0,
  totalCount,
  selectedItems = [],
  isAllSelected = false,
  
  // Actions
  actions = [],
  onSelectAll,
  onClearSelection,
  
  // Positioning
  position = 'bottom',
  sticky = false,
  
  // Progress tracking
  showProgress = false,
  progressInfo,
  onCancelOperation,
  
  // Undo system
  undoActions = [],
  onUndo,
  
  // Localization
  locale = 'es',
  
  // Styling
  className,
  
  // Animation
  disableAnimation = false,
  
  // Accessibility
  ariaLabel,
  ariaLiveRegion = 'polite',
  
  // Custom content
  customContent,
  
  // Validation
  onValidateAction,
  
  // Event handlers
  onActionStart,
  onActionComplete,
  onActionError
}: BirhausBulkActionBarProps<T>) {
  
  const [actionState, setActionState] = useState<BulkActionState>('idle')
  const [executingAction, setExecutingAction] = useState<string | null>(null)
  const [recentUndo, setRecentUndo] = useState<UndoAction | null>(null)
  const [mounted, setMounted] = useState(false)
  
  // BIRHAUS Principle: Miller's Law validation
  const validatedActions = useMemo(() => {
    if (actions.length > 4) {
      console.warn('BIRHAUS Warning: BulkActionBar has more than 4 actions, violating Miller\'s Law. Consider grouping actions or using progressive disclosure.')
    }
    return actions.slice(0, 4) // Enforce hard limit
  }, [actions])
  
  // Mount detection for portal rendering
  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])
  
  // Get localized text
  const getLabel = useCallback((es: string, en?: string) => {
    return locale === 'es' ? es : (en || es)
  }, [locale])
  
  // Handle action execution with BIRHAUS undo patterns
  const handleAction = useCallback(async (action: BirhausBulkAction<T>) => {
    if (action.disabled || actionState === 'executing') return
    
    // BIRHAUS Principle: Validation before execution
    if (onValidateAction) {
      const validation = await onValidateAction(action, selectedItems)
      if (!validation.valid) {
        console.warn('Action validation failed:', validation.message)
        return
      }
    }
    
    setActionState('executing')
    setExecutingAction(action.id)
    onActionStart?.(action, selectedItems)
    
    try {
      let result: any
      
      // Execute action based on type (discriminated union)
      if (isSimpleAction(action)) {
        // Simple action - no undo support
        result = await action.onClick(selectedItems)
      } else if (isUndoableAction(action)) {
        // Undoable action - returns undo function
        result = await action.onClick(selectedItems)
        
        // BIRHAUS Principle: Undo-over-confirm - create undo action
        if (result?.undoAction) {
          setRecentUndo({
            id: `undo-${action.id}-${Date.now()}`,
            labelEs: `Deshacer ${action.labelEs.toLowerCase()}`,
            labelEn: `Undo ${(action.labelEn || action.labelEs).toLowerCase()}`,
            action: result.undoAction,
            timeout: action.undoTimeout || 10000
          })
          
          // Auto-hide undo after timeout
          setTimeout(() => {
            setRecentUndo(null)
          }, action.undoTimeout || 10000)
        }
      }
      
      setActionState('completed')
      onActionComplete?.(action, selectedItems, result)
      
      // Auto-reset state after brief success indication
      setTimeout(() => {
        if (action.clearSelectionOnComplete !== false) {
          onClearSelection?.()
        }
        setActionState('idle')
        setExecutingAction(null)
      }, 1000)
      
    } catch (error) {
      console.error('Bulk action error:', error)
      setActionState('error')
      onActionError?.(action, selectedItems, error as Error)
      
      // Reset state after error display
      setTimeout(() => {
        setActionState('idle')
        setExecutingAction(null)
      }, 3000)
    }
  }, [actionState, onValidateAction, selectedItems, onActionStart, onActionComplete, onActionError, onClearSelection])
  
  // Handle undo action
  const handleUndo = useCallback(async () => {
    if (!recentUndo || !onUndo) return
    
    try {
      await onUndo(recentUndo)
      setRecentUndo(null)
    } catch (error) {
      console.error('Undo action error:', error)
    }
  }, [recentUndo, onUndo])
  
  // Render individual action button
  const renderActionButton = (action: BirhausBulkAction<T>) => {
    const isExecuting = executingAction === action.id
    const isCompleted = actionState === 'completed' && executingAction === action.id
    const isError = actionState === 'error' && executingAction === action.id
    
    return (
      <BirhausButton
        key={action.id}
        variant={action.variant || 'secondary'}
        size="sm"
        onClick={() => handleAction(action)}
        disabled={
          action.disabled || 
          (actionState === 'executing' && !isExecuting) ||
          selectedCount === 0
        }
        className={cn(
          "transition-all duration-200",
          isCompleted && "bg-green-50 border-green-200 text-green-700",
          isError && "bg-red-50 border-red-200 text-red-700"
        )}
        data-birhaus-bulk-action={action.id}
      >
        {isExecuting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
        {isCompleted && <Check className="w-4 h-4 mr-1.5" />}
        {isError && <AlertTriangle className="w-4 h-4 mr-1.5" />}
        {!isExecuting && !isCompleted && !isError && action.icon && (
          <action.icon className="w-4 h-4 mr-1.5" />
        )}
        {getLabel(action.labelEs, action.labelEn)}
      </BirhausButton>
    )
  }
  
  // Don't render if no items selected (unless showing progress)
  if (!mounted || (selectedCount === 0 && !showProgress && !recentUndo)) return null
  
  const content = (
    <div
      role="region"
      aria-label={ariaLabel || getLabel(
        `Acciones masivas: ${selectedCount} elementos seleccionados`,
        `Bulk actions: ${selectedCount} items selected`
      )}
      aria-live={ariaLiveRegion}
    >
      <AnimatePresence>
        {(selectedCount > 0 || showProgress || recentUndo) && (
          <motion.div
            initial={disableAnimation ? {} : { 
              y: position === 'bottom' ? 100 : -100, 
              opacity: 0 
            }}
            animate={{ y: 0, opacity: 1 }}
            exit={disableAnimation ? {} : { 
              y: position === 'bottom' ? 100 : -100, 
              opacity: 0 
            }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className={cn(
              "fixed left-0 right-0 z-50 px-4",
              position === 'bottom' ? 'bottom-4' : 'top-20',
              sticky && "sticky",
              className
            )}
          >
            <div className="max-w-6xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 backdrop-blur-sm bg-white/95">
                {/* Progress bar if showing progress */}
                {showProgress && progressInfo && (
                  <div className="px-6 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">
                        {getLabel(
                          `Procesando ${progressInfo.current} de ${progressInfo.total}...`,
                          `Processing ${progressInfo.current} of ${progressInfo.total}...`
                        )}
                      </span>
                      {onCancelOperation && (
                        <BirhausButton
                          variant="ghost"
                          size="xs"
                          onClick={onCancelOperation}
                        >
                          {getLabel('Cancelar', 'Cancel')}
                        </BirhausButton>
                      )}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(progressInfo.current / progressInfo.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
                
                <div className="flex items-center justify-between p-4">
                  {/* Left side - Selection info */}
                  <div className="flex items-center gap-4">
                    {/* Close button */}
                    <button
                      onClick={onClearSelection}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                      aria-label={getLabel('Limpiar selección', 'Clear selection')}
                      disabled={actionState === 'executing'}
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>

                    {/* Selection toggle */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={isAllSelected ? onClearSelection : onSelectAll}
                        className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                        disabled={actionState === 'executing'}
                      >
                        {isAllSelected ? (
                          <CheckSquare className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                        <span>
                          {selectedCount} {getLabel(
                            selectedCount === 1 ? 'seleccionado' : 'seleccionados',
                            selectedCount === 1 ? 'selected' : 'selected'
                          )}
                          {totalCount && ` ${getLabel('de', 'of')} ${totalCount}`}
                        </span>
                      </button>

                      {/* Select all button */}
                      {totalCount && selectedCount < totalCount && onSelectAll && (
                        <button
                          onClick={onSelectAll}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                          disabled={actionState === 'executing'}
                        >
                          {getLabel('Seleccionar todos', 'Select all')}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Right side - Actions */}
                  <div className="flex items-center gap-2">
                    {customContent}
                    
                    {/* Undo button */}
                    {recentUndo && (
                      <BirhausButton
                        variant="secondary"
                        size="sm"
                        onClick={handleUndo}
                        className="bg-yellow-50 border-yellow-200 text-yellow-800 hover:bg-yellow-100"
                      >
                        <Undo2 className="w-4 h-4 mr-1.5" />
                        {getLabel(recentUndo.labelEs, recentUndo.labelEn)}
                      </BirhausButton>
                    )}
                    
                    {/* Bulk action buttons */}
                    {validatedActions.map(renderActionButton)}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )

  // Portal render for better z-index management
  if (typeof window !== 'undefined' && document.body && !sticky) {
    return createPortal(content, document.body)
  }

  return content
}

export default BirhausBulkActionBar